/**
 * THE DELIVERY PROMISE, UNDER THE PRICE.
 *
 * Seen on 2026-09-17, the first day Meta ads sent shoppers to DK Store: the ad
 * promised free home delivery, and the product page said so only inside its
 * third tab, about 1,100 px below the price. So one short line stands under the
 * price — "Free delivery · 2–7 business days · 14-day returns" — made of what
 * the page already loads: the shipping options its delivery tab asks for (one
 * request between them, useShippingOptions) and the returns promise that tab
 * prints. Never any one shop's values. A part the shop has not set is left out;
 * with nothing set there is no line.
 */
import React, { useEffect, useState } from 'react';
import { FiClock, FiRotateCcw, FiTruck } from 'react-icons/fi';
import useShippingOptions, { DELIVERY_LOCATION_EVENT, initialDeliveryLocation } from '../../hooks/useShippingOptions';
import { translateShippingEta } from '../../utils/shippingCopy';
import { shopPrice } from '../../utils/currency';
import styles from './deliveryPromiseLine.module.css';

const positive = (n) => {
  const v = Number(n);
  return Number.isFinite(v) && v > 0 ? v : null;
};

/**
 * What the line says, as data.
 *
 * `delivery` is the cheapest delivery's price: free, free over the lowest
 * threshold the shop set (a tier's `freeAbove` or its free-delivery promotion),
 * or the price itself. `eta` is that same delivery's time. `returnsDays` is a
 * window the shop stated — not a refusal, not a window left blank, and never our
 * platform default, which promises no window on anyone's behalf.
 */
export function deliveryPromiseParts({ options, returns, freeDeliveryThreshold } = {}) {
  const deliveries = (Array.isArray(options) ? options : [])
    .filter((o) => o && o.method === 'DELIVERY' && Number.isFinite(Number(o.price)));
  const cheapest = deliveries.reduce((best, o) => (!best || Number(o.price) < Number(best.price) ? o : best), null);

  let delivery = null;
  if (cheapest) {
    if (Number(cheapest.price) === 0) {
      delivery = { kind: 'free' };
    } else {
      const thresholds = [...deliveries.map((o) => positive(o.freeAbove)), positive(freeDeliveryThreshold)].filter(Boolean);
      delivery = thresholds.length
        ? { kind: 'freeOver', amount: Math.min(...thresholds) }
        : { kind: 'price', amount: Number(cheapest.price) };
    }
  }

  const eta = cheapest && typeof cheapest.etaCopy === 'string' && cheapest.etaCopy.trim() ? cheapest.etaCopy.trim() : null;
  const returnsDays = returns && returns.allowed === true && !returns.isDefault ? positive(returns.windowDays) : null;
  return { delivery, eta, returnsDays };
}

/** The location the delivery tab prices for, followed when the shopper changes it there. */
function useDeliveryLocation() {
  const [location, setLocation] = useState(initialDeliveryLocation);
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const follow = (e) => setLocation(e.detail ?? null);
    window.addEventListener(DELIVERY_LOCATION_EVENT, follow);
    return () => window.removeEventListener(DELIVERY_LOCATION_EVENT, follow);
  }, []);
  return location;
}

const DeliveryPromiseLine = ({ salonSlug, variant, returns, freeDeliveryThreshold, currency, t }) => {
  const location = useDeliveryLocation();
  const resolved = useShippingOptions(salonSlug, variant?.id, location);
  const { delivery, eta, returnsDays } = deliveryPromiseParts({
    options: resolved?.options,
    returns,
    freeDeliveryThreshold,
  });
  if (!delivery && !eta && !returnsDays) return null;

  const deliveryText = !delivery
    ? null
    : delivery.kind === 'free'
      ? t('productDetail.deliveryPromise.free', { defaultValue: 'Free delivery' })
      : delivery.kind === 'freeOver'
        ? t('productDetail.deliveryPromise.freeOver', { defaultValue: 'Free delivery over {{amount}}', amount: shopPrice(delivery.amount, currency) })
        : t('productDetail.deliveryPromise.price', { defaultValue: 'Delivery {{amount}}', amount: shopPrice(delivery.amount, currency) });

  /* A ROW OF PROMISES, NOT A SENTENCE OF THEM (2026-10-07).
   *
   * The owner: "the free delivery why not showing it a better way?" — and separately, of the
   * returns window, "we should maybe show it nicely and green or something encouraging". This
   * was one grey line of small type under the price, reading "Free delivery · 2–7 business
   * days · 14-day returns", which is the shape of a disclaimer and not of a reason to buy. The
   * reference they sent sets the same facts as a row of icons above short labels, ruled off
   * from the copy around it, and each one reads as a separate promise.
   *
   * Free delivery takes the positive colour because it is the one that is an offer rather than
   * a fact; the other two stay in the page's ink so the row has one emphasis and not three. */
  const cells = [];
  if (deliveryText) {
    cells.push({
      key: 'delivery', Icon: FiTruck, good: true,
      text: deliveryText, testId: 'product-detail-delivery-promise-delivery',
    });
  }
  if (eta) {
    cells.push({
      key: 'eta', Icon: FiClock, good: false,
      text: translateShippingEta(eta, t), testId: 'product-detail-delivery-promise-eta',
    });
  }
  if (returnsDays) {
    cells.push({
      key: 'returns', Icon: FiRotateCcw, good: false,
      text: t('productDetail.deliveryPromise.returns', { defaultValue: '{{count}}-day returns', count: returnsDays }),
      testId: 'product-detail-delivery-promise-returns',
    });
  }

  return (
    <div className={styles.line} data-testid="product-detail-delivery-promise">
      {cells.map(({ key, Icon, good, text, testId }) => (
        <div className={`${styles.part} ${good ? styles.partGood : ''}`} key={key}>
          <Icon className={styles.icon} aria-hidden />
          <span className={styles.partText} data-testid={testId}>{text}</span>
        </div>
      ))}
    </div>
  );
};

export default DeliveryPromiseLine;
